// Stats footer + profile/level modals. Owns renderStats,
// renderSyncDot, openLevelModal/closeLevelModal, renderUserBtn,
// openUserModal/closeUserModal, renderUserModalStats, renderMasteryBar,
// plus the internal computeBoxDistribution helper.
//
// Also fixes a latent bug: openLevelModal previously hardcoded French
// copy even though modal_eval_pending / modal_provisional /
// modal_confirmed i18n keys already existed. This module routes those
// branches through t() so the level modal is fully localized.

import {
  DEFAULT_LEVEL,
  LEVELS,
  LEVEL_MIN_ATTEMPTS,
  LEVEL_PROMOTE_THRESHOLD,
} from "../config/defaults";
import {
  cardsToNextTier,
  computeMasteryTier,
  starsForTier,
} from "../domain/level";
import { computeMasteredCount } from "../domain/session-recap";
import { t } from "../i18n";
import type {
  Card,
  CefrLevel,
  Lang,
  LevelCounts,
  StatsMap,
  SyncStatus,
} from "../types";
import { byId, hide, show } from "./dom";

export type StatsUiDeps = {
  getCards: () => Card[];
  getStats: () => StatsMap;
  getSession: () => { attempts: number; correct: number };
  getGameMode: () => string;
  getDefi: () => { total: number } | null;
  getUserLevel: () => CefrLevel;
  getUserLevelCounts: () => Partial<LevelCounts>;
  getUserName: () => string;
  setUserName: (name: string) => void;
  /** null clears the stored name, string persists it. */
  persistUserName: (name: string | null) => void;
  getSourceLang: () => Lang | null;
  getTargetLang: () => Lang | null;
  getSyncStatus: () => SyncStatus;
  syncEnabled: () => boolean;
  /** Re-render language selects + direction slider — owned by dropdowns.ts. */
  renderLangControls: () => void;
  /** Active deck — used to branch the level-pill display between CEFR
   *  (bilingual) and 5-star mastery tier (mono). */
  getList: () => import("../config/lists").CardList;
};

export type StatsUi = {
  renderStats: () => void;
  renderSyncDot: () => void;
  openLevelModal: () => void;
  closeLevelModal: () => void;
  renderUserBtn: () => void;
  openUserModal: () => void;
  closeUserModal: () => void;
  renderUserModalStats: () => void;
  renderMasteryBar: () => void;
};

// Leitner box distribution across the whole deck. Returns a 6-tuple
// [new, box1, box2, box3, box4, box5] where "new" = unseen cards.
function computeBoxDistribution(cards: Card[], stats: StatsMap): number[] {
  const dist = [0, 0, 0, 0, 0, 0];
  for (const c of cards) {
    const s = stats[c.id];
    if (!s || !s.attempts) {
      dist[0]!++;
    } else {
      const b = Math.min(Math.max(s.box || 1, 1), 5);
      dist[b]!++;
    }
  }
  return dist;
}

export function createStatsUi(deps: StatsUiDeps): StatsUi {
  function renderSyncDot(): void {
    const el = byId("syncDot");
    if (!el) return;
    if (!deps.syncEnabled()) {
      el.className = "sync-dot";
      el.textContent = t("sync_local") as string;
      el.title = t("sync_disabled_title") as string;
      return;
    }
    const status = deps.getSyncStatus();
    const labels: Record<string, string> = {
      idle: t("sync_local") as string,
      synced: t("sync_synced") as string,
      pending: t("sync_pending") as string,
      syncing: t("sync_syncing") as string,
      error: t("sync_error") as string,
      offline: t("sync_offline") as string,
    };
    el.className = "sync-dot " + status;
    el.textContent = labels[status] || status;
    el.title =
      status === "error"
        ? (t("sync_error_title") as string)
        : (t("sync_user_title", deps.getUserName()) as string);
  }

  function renderStats(): void {
    const statsEl = byId("stats");
    if (!statsEl) return;
    const userLevel = deps.getUserLevel() || DEFAULT_LEVEL;
    const sess = deps.getSession();
    const mode = deps.getGameMode();
    const defi = deps.getDefi();
    let sessHtml: string;
    if (mode === "defi" && defi) {
      // Défi: two stacked rows — progression through the challenge on
      // top, accuracy below. Accuracy is skipped before the first
      // attempt so "0/0" never shows. ▶ = progress glyph, ✓ = accuracy.
      const progressRow = `<span class="stats-row">▶ <b>${sess.attempts}</b>/${defi.total}</span>`;
      if (sess.attempts) {
        const pct = Math.round((sess.correct / sess.attempts) * 100);
        const accRow = `<span class="stats-row">✓ <b>${sess.correct}</b>/${sess.attempts} · <b>${pct}%</b></span>`;
        sessHtml = `<span class="stats-stack">${progressRow}${accRow}</span>`;
      } else {
        sessHtml = `<span class="stats-stack">${progressRow}</span>`;
      }
    } else if (sess.attempts) {
      // Libre/adaptatif — single compact line. ✓ glyph makes the
      // meaning self-evident (accuracy); no "cette série" label.
      const pct = Math.round((sess.correct / sess.attempts) * 100);
      sessHtml = `<span>✓ <b>${sess.correct}</b>/${sess.attempts} · <b>${pct}%</b></span>`;
    } else {
      // No attempts yet — render nothing rather than a "en attente"
      // placeholder. The stats row fills itself in after the first
      // answer, which is a clearer signal than a preemptive label.
      sessHtml = "";
    }
    // #syncDot lives as a direct sibling of .stats in .footer-bar so it
    // aligns vertically with the action buttons instead of wrapping to a
    // second line inside .stats. Don't touch it here — renderSyncDot()
    // below updates it in place.
    statsEl.innerHTML = sessHtml;
    // Pill content branches on deck shape: CEFR code for bilingual
    // (coloured by level), 5-star mastery tier for mono (neutral, no
    // per-tier colour — the star fill IS the progress indicator).
    const list = deps.getList();
    const userLevelValue = byId("userLevelValue");
    const userLevelBtn = byId("userLevelBtn");
    if (list.shape === "mono") {
      const mastered = computeMasteredCount(deps.getCards(), deps.getStats());
      const tier = computeMasteryTier(mastered, deps.getCards().length);
      if (userLevelValue) userLevelValue.textContent = starsForTier(tier);
      // "lvl-mono" gets its own CSS rule; no per-tier colour variants
      // so stars alone carry the progression signal.
      if (userLevelBtn) userLevelBtn.className = "level-pill lvl-mono";
    } else {
      if (userLevelValue) userLevelValue.textContent = userLevel;
      if (userLevelBtn) userLevelBtn.className = "level-pill lvl-" + userLevel;
    }
    renderSyncDot();
  }

  function openLevelModal(): void {
    const counts = deps.getUserLevelCounts();
    const userLevel = deps.getUserLevel() || DEFAULT_LEVEL;
    let totalAttempts = 0;
    for (const lv of LEVELS) totalAttempts += counts[lv]?.attempts || 0;

    const estLevel = byId("modalEstLevel");
    if (estLevel) estLevel.textContent = userLevel;

    const desc = byId("modalEstDesc");
    const userCount = counts[userLevel] || {
      attempts: 0,
      correct: 0,
      rate: 0,
    };
    if (desc) {
      if (totalAttempts < LEVEL_MIN_ATTEMPTS) {
        desc.innerHTML = t(
          "modal_eval_pending",
          LEVEL_MIN_ATTEMPTS,
          totalAttempts,
        ) as string;
      } else if (userCount.attempts < LEVEL_MIN_ATTEMPTS) {
        desc.innerHTML = t(
          "modal_provisional",
          userCount.attempts,
          LEVEL_MIN_ATTEMPTS,
        ) as string;
      } else {
        const pct = Math.round((userCount.correct / userCount.attempts) * 100);
        desc.innerHTML = t(
          "modal_confirmed",
          pct,
          userCount.attempts,
          userLevel,
        ) as string;
      }
    }

    // Build per-level breakdown table.
    const rows: string[] = [
      `<tr><th>${t("modal_th_level")}</th><th>${t("modal_th_seen")}</th><th>${t("modal_th_rate")}</th><th></th></tr>`,
    ];
    for (const lv of LEVELS) {
      const c = counts[lv] || { attempts: 0, correct: 0, rate: 0 };
      const pct = c.attempts ? Math.round(c.rate * 100) : 0;
      let cls = "pending";
      let label = "—";
      if (c.attempts >= LEVEL_MIN_ATTEMPTS) {
        cls = c.rate >= LEVEL_PROMOTE_THRESHOLD ? "pass" : "fail";
        label = pct + "%";
      } else if (c.attempts > 0) {
        label = pct + "% " + t("modal_sample");
      }
      const bar = c.attempts
        ? `<span class="modal-bar"><span style="width:${Math.min(100, pct)}%"></span></span>`
        : "";
      rows.push(
        `<tr>` +
          `<td><span class="lv-cell"><span class="level-badge lvl-${lv}">${lv}</span></span></td>` +
          `<td>${c.attempts || 0}</td>` +
          `<td class="${cls}">${label}</td>` +
          `<td>${bar}</td>` +
          `</tr>`,
      );
    }
    const table = byId("levelTable");
    if (table) table.innerHTML = rows.join("");
    show(byId("levelModal"));
  }

  function closeLevelModal(): void {
    hide(byId("levelModal"));
  }

  function renderUserBtn(): void {
    const name = (deps.getUserName() || "").trim();
    const userBtnName = byId("userBtnName");
    if (userBtnName) userBtnName.textContent = name || (t("user_btn_empty") as string);
    const userBtn = byId("userBtn");
    if (userBtn) userBtn.title = t("user_btn_title") as string;
  }

  // Leitner mastery histogram. Stacked horizontal bar partitioned into
  // 6 segments (new + boxes 1-5). Widths are proportional, so the bar
  // visibly shifts right as the user masters more cards.
  function renderMasteryBar(): void {
    const el = byId("userModalMastery");
    if (!el) return;
    const dist = computeBoxDistribution(deps.getCards(), deps.getStats());
    const total = dist.reduce((a, b) => a + b, 0);
    if (!total) {
      el.innerHTML = "";
      return;
    }
    const slotKeys = [
      "mastery_new",
      "mastery_b1",
      "mastery_b2",
      "mastery_b3",
      "mastery_b4",
      "mastery_b5",
    ];
    const segs = dist
      .map((count, i) => {
        const pct = (count / total) * 100;
        if (!pct) return "";
        return (
          `<div class="mastery-seg mastery-seg-${i}" style="width:${pct.toFixed(2)}%" title="${t(slotKeys[i]!)}: ${count}">` +
          `${count > 0 && pct >= 8 ? count : ""}` +
          `</div>`
        );
      })
      .join("");
    const legend = dist
      .map((count, i) => {
        if (!count) return "";
        return (
          `<span class="mastery-legend-item">` +
          `<span class="mastery-legend-swatch mastery-seg-${i}"></span>` +
          `${t(slotKeys[i]!)} <b>${count}</b>` +
          `</span>`
        );
      })
      .join("");
    el.innerHTML =
      `<div class="mastery-label">${t("mastery_label")}</div>` +
      `<div class="mastery-bar">${segs}</div>` +
      `<div class="mastery-legend">${legend}</div>`;
  }

  function renderUserModalStats(): void {
    const userModalStats = byId("userModalStats");
    if (!userModalStats) return;
    const now = Date.now();
    const cards = deps.getCards();
    const stats = deps.getStats();
    const total = cards.length;
    let seen = 0;
    let due = 0;
    let correct = 0;
    let attempts = 0;
    // Box 5 = "acquired" — the closest thing Leitner has to a
    // universally-accepted mastery signal.
    let acquired = 0;
    for (const c of cards) {
      const s = stats[c.id];
      if (s && s.attempts) {
        seen++;
        attempts += s.attempts;
        correct += s.correct || 0;
        if ((s.dueAt || 0) <= now) due++;
        if ((s.box || 1) >= 5) acquired++;
      } else {
        due++;
      }
    }
    const rate = attempts ? Math.round((correct / attempts) * 100) : 0;
    const seenPct = total ? Math.round((seen / total) * 100) : 0;
    const acqPct = total ? Math.round((acquired / total) * 100) : 0;
    const duePct = total ? Math.round((due / total) * 100) : 0;
    const sess = deps.getSession();
    const sessPct = sess.attempts
      ? Math.round((sess.correct / sess.attempts) * 100)
      : 0;
    // Consistent card format: big % on line 1, ratio/count on line 2,
    // label on line 3. Cards without a natural ratio (session empty)
    // still put the % first.
    const card = (val: string, sub: string, lblKey: string): string =>
      `<div class="modal-stat">` +
      `<div class="val">${val}</div>` +
      (sub ? `<div class="sub">${sub}</div>` : `<div class="sub">&nbsp;</div>`) +
      `<div class="lbl">${t(lblKey)}</div>` +
      `</div>`;
    // Mono decks get a tier hero block ABOVE the stats grid: 5 big
    // stars + exact % + gap-to-next. The stats cards below still show
    // seen / rate / session / due / acquired — the tier is a summary,
    // the cards are the detail. Bilingual decks skip the tier (they
    // have the CEFR pill in the header for progression signal).
    let tierHtml = "";
    if (deps.getList().shape === "mono") {
      const tier = computeMasteryTier(acquired, total);
      const stars = starsForTier(tier);
      const toNext = cardsToNextTier(acquired, total);
      const gapLine =
        toNext === null
          ? (t("mastery_tier_max") as string)
          : (t("mastery_tier_gap", toNext) as string);
      tierHtml =
        `<div class="mastery-tier">` +
        `<div class="mastery-tier-stars">${stars}</div>` +
        `<div class="mastery-tier-pct">${acqPct}% · ${acquired}/${total}</div>` +
        `<div class="mastery-tier-gap">${gapLine}</div>` +
        `</div>`;
    }
    userModalStats.innerHTML =
      tierHtml +
      card(`${seenPct}%`, `${seen}/${total}`, "stats_seen") +
      card(`${rate}%`, attempts ? `${correct}/${attempts}` : "", "stats_rate") +
      card(
        sess.attempts ? `${sessPct}%` : "—",
        sess.attempts ? `${sess.correct}/${sess.attempts}` : "",
        "stats_session",
      ) +
      card(`${duePct}%`, `${due}/${total}`, "stats_due") +
      card(`${acqPct}%`, `${acquired}/${total}`, "stats_acquired");
    renderMasteryBar();
  }

  function openUserModal(): void {
    const userModalName = byId<HTMLInputElement>("userModalName");
    if (userModalName) userModalName.value = deps.getUserName() || "";
    // renderLangControls still touches the locale select that lives in
    // this modal (taille texte is its own controller). Lang-pair /
    // direction bits inside renderLangControls update DOM nodes that
    // now live in deck-settings, which is a harmless cross-wired call.
    deps.renderLangControls();
    show(byId("userModal"));
  }

  function closeUserModal(): void {
    // (Profile modal now only hosts user-scoped prefs — name + locale
    // + font-size. No invalid-state guard possible.)
    const userModalName = byId<HTMLInputElement>("userModalName");
    const newName = (userModalName?.value || "").trim();
    if (newName !== deps.getUserName()) {
      deps.setUserName(newName);
      deps.persistUserName(newName || null);
      renderUserBtn();
      renderStats();
    }
    hide(byId("userModal"));
  }

  return {
    renderStats,
    renderSyncDot,
    openLevelModal,
    closeLevelModal,
    renderUserBtn,
    openUserModal,
    closeUserModal,
    renderUserModalStats,
    renderMasteryBar,
  };
}
